import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import StudioGarmentSvg from './StudioGarmentSvg';
import {
  getArtworkBox,
  getGarmentLayout,
  getPrintRect,
  offsetFromDrag,
} from './studio2dConfig';
import { getArtworkConfig } from './merchPlaygroundConfig';

const StudioCanvas2D = ({
  productType,
  productColor,
  view,
  artwork,
  artworkLayers,
  selectedArtworkLayerId,
  selectedArtworkSide,
  artworkTransforms,
  onArtworkSideSelect,
  onArtworkLayerSelect,
  onArtworkLayerTransformChange,
  onArtworkTransformChange,
  onArtworkTransformLimitsChange,
  canvasZoom = 1,
  resetViewSignal = 0,
  compact = false,
  previewOnly = false,
  mockupVariant = 'flat',
}) => {
  const dragRef = useRef(null);
  const panDragRef = useRef(null);
  const [canvasPan, setCanvasPan] = useState({ x: 0, y: 0 });
  const layout = getGarmentLayout(productType);
  const artworkConfig = getArtworkConfig(productType);
  const activeSide = artworkConfig.sides.includes(view) ? view : 'front';
  const artworkEntry = artwork[activeSide];
  const activeLayers = artworkLayers?.[activeSide] || [];
  const transform = artworkTransforms[activeSide] || { offsetX: 0, offsetY: 0, scale: 1 };
  const placementMode = transform.mode === 'expanded' ? 'expanded' : 'standard';
  const printRect = getPrintRect(productType, activeSide, placementMode);
  const artworkBox = getArtworkBox(productType, activeSide, transform);
  const isDarkGarment = productColor?.toLowerCase?.() === '#151515' || productColor?.toLowerCase?.() === 'black';

  useEffect(() => {
    setCanvasPan({ x: 0, y: 0 });
  }, [activeSide, productType]);

  useEffect(() => {
    if (canvasZoom < 1) setCanvasPan({ x: 0, y: 0 });
  }, [canvasZoom]);

  useEffect(() => {
    setCanvasPan({ x: 0, y: 0 });
  }, [resetViewSignal]);

  useEffect(() => {
    onArtworkTransformLimitsChange?.(activeSide, {
      offsetX: { min: -1, max: 1 },
      offsetY: { min: -1, max: 1 },
    });
  }, [activeSide, onArtworkTransformLimitsChange, productType]);

  const handlePointerDown = useCallback((event) => {
    if (!artworkEntry?.url && !activeLayers.length) return;
    event.preventDefault();
    onArtworkSideSelect?.(activeSide);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      type: 'stencil',
      baseTransform: transform,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [activeLayers.length, activeSide, artworkEntry?.url, onArtworkSideSelect, transform]);

  const handleLayerPointerDown = useCallback((event, layer) => {
    event.preventDefault();
    event.stopPropagation();
    onArtworkSideSelect?.(activeSide);
    onArtworkLayerSelect?.(activeSide, layer.id);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      type: 'layer',
      layerId: layer.id,
      layer,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [activeSide, onArtworkLayerSelect, onArtworkSideSelect]);

  const handleLayerResizePointerDown = useCallback((event, layer, corner) => {
    event.preventDefault();
    event.stopPropagation();
    onArtworkSideSelect?.(activeSide);
    onArtworkLayerSelect?.(activeSide, layer.id);
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      type: 'layer-resize',
      layerId: layer.id,
      layer,
      corner,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [activeSide, onArtworkLayerSelect, onArtworkSideSelect]);

  const handlePointerMove = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    if (drag.type === 'layer-resize') {
      const directionX = drag.corner.includes('right') ? 1 : -1;
      const directionY = drag.corner.includes('bottom') ? 1 : -1;
      const baseScaleX = drag.layer.scaleX ?? drag.layer.scale ?? 1;
      const baseScaleY = drag.layer.scaleY ?? drag.layer.scale ?? 1;
      const horizontalChange = (deltaX * directionX) / printRect.width;
      const verticalChange = (deltaY * directionY) / printRect.height;
      const proportionalChange = Math.abs(horizontalChange) >= Math.abs(verticalChange)
        ? horizontalChange
        : verticalChange;
      const nextScaleX = Math.max(0.05, Math.min(2, baseScaleX + (event.shiftKey ? horizontalChange : proportionalChange)));
      const nextScaleY = Math.max(0.05, Math.min(2, baseScaleY + (event.shiftKey ? verticalChange : proportionalChange)));
      const widthChange = nextScaleX - baseScaleX;
      const heightChange = nextScaleY - baseScaleY;
      onArtworkLayerTransformChange?.(activeSide, drag.layerId, {
        scale: nextScaleX,
        scaleX: nextScaleX,
        scaleY: nextScaleY,
        x: (drag.layer.x || 0) + directionX * widthChange,
        y: (drag.layer.y || 0) + directionY * heightChange,
      });
      return;
    }
    if (drag.type === 'layer') {
      onArtworkLayerTransformChange?.(activeSide, drag.layerId, {
        x: (drag.layer.x || 0) + (deltaX / printRect.width) * 2,
        y: (drag.layer.y || 0) + (deltaY / printRect.height) * 2,
      });
      return;
    }
    const next = offsetFromDrag(productType, activeSide, drag.baseTransform, deltaX, deltaY, compact ? 0.55 : 1);
    onArtworkTransformChange?.(activeSide, next);
  }, [activeSide, compact, onArtworkLayerTransformChange, onArtworkTransformChange, printRect.height, printRect.width, productType]);

  const handlePointerUp = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const handleCanvasPointerDown = useCallback((event) => {
    if (compact || canvasZoom < 1 || event.button !== 0) return;
    if (event.target.closest('.studio-canvas-2d-artwork, .studio-canvas-2d-layer')) return;
    event.preventDefault();
    panDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      baseX: canvasPan.x,
      baseY: canvasPan.y,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }, [canvasPan.x, canvasPan.y, canvasZoom, compact]);

  const handleCanvasPointerMove = useCallback((event) => {
    const drag = panDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    const panLimit = 80 + Math.max(0, canvasZoom - 1) * 360;
    setCanvasPan({
      x: Math.max(-panLimit, Math.min(panLimit, drag.baseX + event.clientX - drag.startX)),
      y: Math.max(-panLimit, Math.min(panLimit, drag.baseY + event.clientY - drag.startY)),
    });
  }, [canvasZoom]);

  const handleCanvasPointerUp = useCallback((event) => {
    const drag = panDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    panDragRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const variantClass = mockupVariant !== 'flat' ? `is-${mockupVariant}` : '';

  return (
    <div
      className={`studio-canvas-2d is-${productType} ${compact ? 'is-compact' : ''} ${previewOnly ? 'is-preview-only' : ''} ${!compact && canvasZoom >= 1 ? 'is-pannable' : ''} ${variantClass}`}
      style={compact ? { width: '100%', height: '100%', minHeight: 0, aspectRatio: 'auto' } : undefined}
      data-testid="studio-canvas-2d"
      aria-label="2D merch preview"
      onPointerDown={handleCanvasPointerDown}
      onPointerMove={handleCanvasPointerMove}
      onPointerUp={handleCanvasPointerUp}
      onPointerCancel={handleCanvasPointerUp}
    >
      <div
        className="studio-canvas-2d-frame"
        style={{
          width: layout.width,
          height: layout.height,
          '--studio-canvas-zoom': Math.max(0.55, Math.min(1.8, canvasZoom)),
          '--studio-canvas-pan-x': `${canvasPan.x}px`,
          '--studio-canvas-pan-y': `${canvasPan.y}px`,
        }}
      >
        <StudioGarmentSvg
          productType={productType}
          view={activeSide}
          fill={productColor}
          className="studio-canvas-2d-garment"
          showDetails={false}
        />
        {!previewOnly && (
          <div
            className={`studio-canvas-2d-print-area ${isDarkGarment ? 'is-light' : ''}`}
            style={{
              left: printRect.left,
              top: printRect.top,
              width: printRect.width,
              height: printRect.height,
            }}
            aria-hidden="true"
          />
        )}
        {activeLayers.length > 0 ? (
          <div
            className="studio-canvas-2d-layer-stage"
            style={{
              left: printRect.left,
              top: printRect.top,
              width: printRect.width,
              height: printRect.height,
            }}
            data-testid={compact ? undefined : `studio-artwork-${activeSide}`}
          >
            {activeLayers.map((layer, layerIndex) => {
              const layerWidth = Math.max(0.05, layer.scaleX ?? layer.scale ?? 1) * 100;
              const layerAspectRatio = Number.isFinite(layer.aspectRatio) && layer.aspectRatio > 0
                ? layer.aspectRatio
                : null;
              const layerHeight = layerAspectRatio
                ? (layerWidth * (printRect.width / printRect.height)) / layerAspectRatio
                : Math.max(0.05, layer.scaleY ?? layer.scale ?? 1) * 100;
              return (
              <button
                key={layer.id}
                type="button"
                className={`studio-canvas-2d-layer ${selectedArtworkLayerId === layer.id ? 'is-selected' : ''}`}
                style={{
                  left: `${50 + (layer.x || 0) * 50}%`,
                  top: `${50 + (layer.y || 0) * 50}%`,
                  width: `${layerWidth}%`,
                  height: `${layerHeight}%`,
                  zIndex: activeLayers.length - layerIndex,
                }}
                onPointerDown={(event) => handleLayerPointerDown(event, layer)}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                aria-label={`Drag ${layer.name}`}
              >
                <img
                  src={layer.url}
                  alt=""
                  draggable={false}
                  onLoad={(event) => {
                    if (layer.aspectRatio) return;
                    const ratio = event.currentTarget.naturalWidth / Math.max(1, event.currentTarget.naturalHeight);
                    onArtworkLayerTransformChange?.(activeSide, layer.id, { aspectRatio: ratio });
                  }}
                />
                {selectedArtworkLayerId === layer.id && !compact && (
                  ['top-left', 'top-right', 'bottom-left', 'bottom-right'].map((corner) => (
                    <span
                      key={corner}
                      className={`studio-canvas-2d-layer-resize is-${corner}`}
                      aria-hidden="true"
                      onPointerDown={(event) => handleLayerResizePointerDown(event, layer, corner)}
                      onPointerMove={handlePointerMove}
                      onPointerUp={handlePointerUp}
                      onPointerCancel={handlePointerUp}
                    />
                  ))
                )}
              </button>
              );
            })}
          </div>
        ) : artworkEntry?.url && (
          <button
            type="button"
            className={`studio-canvas-2d-artwork ${selectedArtworkSide === activeSide ? 'is-selected' : ''}`}
            style={{
              left: artworkBox.x,
              top: artworkBox.y,
              width: artworkBox.width,
              height: artworkBox.height,
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            aria-label={`Drag ${activeSide} artwork`}
            data-testid={compact ? undefined : `studio-artwork-${activeSide}`}
          >
            <img src={artworkEntry.url} alt="" draggable={false} />
          </button>
        )}
      </div>
    </div>
  );
};

StudioCanvas2D.propTypes = {
  productType: PropTypes.string.isRequired,
  productColor: PropTypes.string.isRequired,
  view: PropTypes.string.isRequired,
  artwork: PropTypes.object.isRequired,
  artworkLayers: PropTypes.object,
  selectedArtworkLayerId: PropTypes.string,
  selectedArtworkSide: PropTypes.string.isRequired,
  artworkTransforms: PropTypes.object.isRequired,
  onArtworkSideSelect: PropTypes.func,
  onArtworkLayerSelect: PropTypes.func,
  onArtworkLayerTransformChange: PropTypes.func,
  onArtworkTransformChange: PropTypes.func,
  onArtworkTransformLimitsChange: PropTypes.func,
  canvasZoom: PropTypes.number,
  resetViewSignal: PropTypes.number,
  compact: PropTypes.bool,
  previewOnly: PropTypes.bool,
  mockupVariant: PropTypes.string,
};

export default StudioCanvas2D;
